import React, { ChangeEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Collapse,
    Container,
    Fade,
    FormControlLabel,
    Link,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";

import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Timeout } from "react-number-format/types/types";
import { signOut, fetchAuthSession, getCurrentUser } from "aws-amplify/auth";

import { RootState } from "@store/store";
import { Loader } from "@components/Loader";
import { CustomSwitch } from "@components/CustomSwitch";
import { logout, setSignInStep, verifiedOTP } from "@store/slices/sessionSlice";
import { usePostOTPMutation, usePostValidOTPMutation } from "@services/api/security";
import { useLazyGetCurrentUserInfoV2Query, usePatchUserMutation } from "@services/api/users";

interface FormularioMFAProps {
    // email: string;
}

export const FormularioMFA = (props: FormularioMFAProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const hasFetched = useRef(false);

    const inputRefs = [
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
        useRef<HTMLInputElement>(null),
    ];

    const [intervalId, setIntervalId] = useState<Timeout>();
    const [resendTimeout, setResendTimeout] = useState<number>(120);

    const [error, setError] = useState<boolean>(false);
    const [redirected, setRedirected] = useState<boolean>(false);
    const [errroMsg, setErrorMsg] = useState<ReactNode>(undefined);

    const [keepSessionActive, setKeepSessionActive] = useState<boolean>(false);

    const sessionInfo = useSelector((state: RootState) => state.session);

    const [triggerPostOTP] = usePostOTPMutation();
    const [triggerPatchUser] = usePatchUserMutation();
    const [triggerPostValidOTP] = usePostValidOTPMutation();
    const [triggerGetCurrentUserInfo] = useLazyGetCurrentUserInfoV2Query();

    const formik = useFormik({
        initialValues: {
            code1: "",
            code2: "",
            code3: "",
            code4: "",
            code5: "",
            code6: "",
            persistSession: false,
        },
        validationSchema: yup.object().shape({
            code1: yup.number().required("Requerido"),
            code2: yup.number().required("Requerido"),
            code3: yup.number().required("Requerido"),
            code4: yup.number().required("Requerido"),
            code5: yup.number().required("Requerido"),
            code6: yup.number().required("Requerido"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                setError(false);
                setErrorMsg(undefined);

                const result = Object.values(values).reduce((pre, curr) => pre + Number(curr), 0);

                if (import.meta.env.PROD) {
                    await triggerPostValidOTP({
                        code:
                            values.code1.toString() +
                            values.code2.toString() +
                            values.code3.toString() +
                            values.code4.toString() +
                            values.code5.toString() +
                            values.code6.toString(),
                        codeType: "login",
                    }).unwrap();
                } else {
                    if (result !== 21) {
                        throw { status: 400, message: "Código Incorrecto" };
                    }
                }

                const currentUser = await getCurrentUser();
                const userInfo = await triggerGetCurrentUserInfo(currentUser.userId).unwrap();

                await triggerPatchUser({
                    userId: userInfo.data.user_data.id,
                    body: {
                        preferences: {
                            ...userInfo.data.user_data.preferences,
                            sessionOptions: {
                                ...userInfo.data.user_data.preferences.sessionOptions,
                                persist: keepSessionActive,
                            },
                        },
                    },
                }).unwrap();

                setRedirected(true);
                setTimeout(() => {
                    dispatch(verifiedOTP());
                    dispatch(setSignInStep("WELCOME"));
                    navigate("/solicitudes");
                }, 2000);
            } catch (error: any) {
                if (error.status === 400) {
                    setError(true);
                    setErrorMsg("Código incorrecto");
                    formik.setErrors({
                        code1: "a",
                        code2: "a",
                        code3: "s",
                        code4: "a",
                        code5: "a",
                        code6: "a",
                    });
                }

                if (error.status === 404) {
                    if (error.data.data.attemps === 3) {
                        await signOut();
                        dispatch(logout());
                        dispatch(setSignInStep("BLOCKED"));
                    } else {
                        setError(true);
                        setErrorMsg(error.data.message);
                        formik.setErrors({
                            code1: "a",
                            code2: "a",
                            code3: "s",
                            code4: "a",
                            code5: "a",
                            code6: "a",
                        });
                    }
                }

                setTimeout(() => {
                    setErrorMsg(undefined);
                }, 2500);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const hasFormikErrors = useMemo(() => {
        return Object.keys(formik.errors).length > 0;
    }, [formik.errors, formik.touched]);

    const handlesetKeepSessionActiveChanged = (_: any, checked: boolean) => {
        setKeepSessionActive(checked);
    };

    const restartTimer = () => {
        clearInterval(intervalId);
        const interId = setInterval(() => {
            setResendTimeout((oldValue) => oldValue - 1);
        }, 1000);

        setIntervalId(interId);
    };

    const handleResendCodeClicked = async (e: any) => {
        try {
            e.preventDefault();
            setResendTimeout(120);
            await triggerPostOTP("login").unwrap();
            restartTimer();
        } catch (error: any) {
            setError(true);
            setErrorMsg(error.data.message);

            setTimeout(() => {
                setErrorMsg(undefined);
            }, 2500);
        }
    };

    const handleKeyDownCode = (event: any, index: number) => {
        if (event.key === "Backspace" && formik.values[event.target.name].length === 0) {
            inputRefs[index - 1]?.current?.focus();
        }
    };

    const handlePasteCode = async (event: React.ClipboardEvent, index: number) => {
        event.preventDefault();
        const digits = event.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, 6 - index);
        for (let i = 0; i < digits.length; i++) {
            await formik.setFieldValue(`code${index + i + 1}`, digits[i], true);
        }
        const lastFilledIndex = Math.min(index + digits.length - 1, 5);
        inputRefs[lastFilledIndex]?.current?.focus();
        setError(false);
    };

    const handleChangeCode = async (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
        const name = event.target.name;
        const digitsOnly = event.target.value.replace(/\D/g, "");

        if (event.target.value.length === 1) {
            await formik.setFieldValue(`code${index + 2}`, "", false);
            inputRefs[index + 1]?.current?.focus();
        }

        await formik.setFieldValue(name, digitsOnly, true);
        setError(false);
    };

    const handleLogout = async (e: any) => {
        e.preventDefault();
        await signOut();
        dispatch(logout());
        dispatch(setSignInStep("LOGIN"));
    };

    const requestOTP = async () => {
        try {
            const authSession = await fetchAuthSession();

            if (authSession.tokens === undefined) {
                await signOut();
                dispatch(logout());
                return;
            }

            await triggerPostOTP("login").unwrap();
            formik.resetForm({
                values: {
                    code1: "",
                    code2: "",
                    code3: "",
                    code4: "",
                    code5: "",
                    code6: "",
                },
            });
            setError(false);
            restartTimer();
        } catch (error: any) {
            if (error.status === 429) {
                setError(true);
                setErrorMsg(
                    <Typography component="span" sx={{ alignContent: "center", alignItems: "center" }}>
                        Número máximo de reenvíos de código alcanzado.{" "}
                        <Link component={"button"} onClick={handleLogout} sx={{ mt: -1 }}>
                            Regresar a inicio de sesión
                        </Link>
                    </Typography>,
                );
            } else {
                console.log(error);
                setError(true);
                setErrorMsg("Ocurrio un error al envíar el código OTP.");
            }
        }
    };

    useEffect(() => {
        if (resendTimeout === 0) {
            clearInterval(intervalId);
        }
    }, [resendTimeout]);

    useEffect(() => {
        if (hasFetched.current) return;

        setKeepSessionActive(sessionInfo.user.preferences.sessionOptions.persist);
        requestOTP();

        hasFetched.current = true;
    }, [hasFetched]);

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Stack my={2} mx={4} direction={"column"} justifyContent={"flex-start"} alignItems={"center"}>
                <Typography variant="h2" fontWeight={700} color="#002652">
                    {redirected ? "Bienvenido de nuevo" : "¡Hola!"}
                </Typography>

                <Collapse in={redirected} unmountOnExit>
                    <Box
                        width={"100%"}
                        height={"200px"}
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"center"}
                    >
                        <Box>
                            <Loader height="100%" />
                        </Box>
                    </Box>
                </Collapse>
                <Collapse in={!redirected} unmountOnExit>
                    <Box display={"flex"} alignItems={"center"} flexDirection={"column"}>
                        <Typography mt={3} textAlign={"center"} variant="body1" color="#002652">
                            Ingresa el código que te enviamos vía correo electrónico
                        </Typography>
                        <Typography color="main2" variant="body1" fontWeight={700} my={4}>
                            {localStorage.getItem("email") ?? ""}
                        </Typography>
                        <Stack direction={"row"} alignItems={"center"} columnGap={1}>
                            {["code1", "code2", "code3", "code4", "code5", "code6"].map((code, idx) => (
                                <>
                                    <TextField
                                        key={code}
                                        name={code}
                                        type="text"
                                        inputMode="numeric"
                                        inputRef={inputRefs[idx]}
                                        onKeyDown={(event) => handleKeyDownCode(event, idx)}
                                        onChange={(event) => handleChangeCode(event, idx)}
                                        onPaste={(event) => handlePasteCode(event, idx)}
                                        onBlur={formik.handleBlur}
                                        value={formik.values[code]}
                                        autoComplete="off"
                                        sx={{
                                            backgroundColor: error
                                                ? theme.palette.secondary3.light
                                                : theme.palette.main2.main,
                                            borderRadius: 2,
                                            height: 75,
                                            width: 50,
                                            "& .MuiInputBase-input": {
                                                fontSize: "2rem",
                                                position: "relative",
                                                textAlign: "center",
                                                top: "-2px",
                                            },
                                            "& .MuiOutlinedInput-notchedOutline": { height: 75, border: 0 },
                                        }}
                                        slotProps={{
                                            htmlInput: {
                                                maxLength: 1,
                                                autoComplete: "new-password",
                                                pattern: "[0-9]*",
                                            },
                                        }}
                                    />
                                    {code === "code3" && (
                                        <Typography color="main2" fontWeight={700}>
                                            -
                                        </Typography>
                                    )}
                                </>
                            ))}
                        </Stack>
                        <Collapse in={error && errroMsg !== undefined} sx={{ width: "100%" }}>
                            <Box sx={{ mt: 1 }} justifyContent={"center"} display={"flex"}>
                                <Alert variant="outlined" severity="error" sx={{ textAlign: "center", width: "100%" }}>
                                    {errroMsg}
                                </Alert>
                            </Box>
                        </Collapse>
                        <Typography variant="body1" fontWeight={700} color="main2" my={3}>
                            {Math.floor(resendTimeout / 60)
                                .toString()
                                .padStart(2, "0")}
                            :{(resendTimeout % 60).toString().padStart(2, "0")} {resendTimeout < 60 ? "seg" : "min"}.
                        </Typography>

                        <Fade in={resendTimeout === 0} unmountOnExit>
                            <Button onClick={handleResendCodeClicked}>Reenviar código</Button>
                        </Fade>
                        <FormControlLabel
                            sx={{ color: theme.palette.main3.main, fontWeight: 400, my: 3 }}
                            value={formik.values.persistSession}
                            control={
                                <CustomSwitch
                                    onChange={handlesetKeepSessionActiveChanged}
                                    checked={keepSessionActive}
                                />
                            }
                            label={"Mantener sesión iniciada durante 15 días"}
                        />
                        <Container fixed sx={{ display: "flex", flexDirection: "row", columnGap: 1 }}>
                            <Button variant="contained" fullWidth disabled={formik.isSubmitting} onClick={handleLogout}>
                                Regresar
                            </Button>
                            <Button
                                variant="contained"
                                type="submit"
                                fullWidth
                                disabled={!formik.dirty || hasFormikErrors}
                                loading={formik.isSubmitting}
                            >
                                Validar código
                            </Button>
                        </Container>
                    </Box>
                </Collapse>
            </Stack>
        </form>
    );
};
